// AUDITORIA-CLINICA-REDISENO (FASE 1) -- modelo COMPARTIDO de las 7 secciones
// clinicas corregibles. Es el unico lugar donde se decide "esta seccion tiene
// registro en esta atencion" y "que se muestra como valor", para que el indice
// lateral, las tarjetas del expediente y el bloque "sin registro" no puedan
// discrepar entre si.
//
// NO toca la API ni el modelo de datos: se alimenta EXCLUSIVAMENTE de lo que
// ya devuelve `GET /audit/clinical/appointments/:idcita` (campo `secciones`) y
// `GET .../history`. El catalogo y el orden de secciones replican los de
// `SECTIONS` en back_smartmedic/src/function/FAuditClinicalCorrection.ts --
// nunca se inventa una seccion que el backend no sepa corregir.

export const SECCIONES_AUDITORIA = [
    { id: "motivo_consulta", label: "Motivo de consulta" },
    { id: "signos_vitales", label: "Signos vitales" },
    { id: "enfermedad_actual", label: "Enfermedad actual" },
    { id: "diagnosticos", label: "Diagnósticos" },
    { id: "plan_tratamiento", label: "Plan de tratamiento" },
    { id: "nota_evolucion", label: "Nota de evolución" },
    { id: "recetas", label: "Recetas" },
];

export const SECCION_LABEL = SECCIONES_AUDITORIA.reduce((acc, s) => ({ ...acc, [s.id]: s.label }), {});

export const TOTAL_SECCIONES = SECCIONES_AUDITORIA.length;

const esTextoConContenido = (v) => typeof v === "string" && v.trim() !== "";
const lista = (v) => (Array.isArray(v) ? v : []);

/**
 * ¿El médico registró algo en esta sección de esta atención?
 * `motivo_consulta` es un campo de `citas` (texto, puede venir null/vacío);
 * las otras 6 son colecciones -- tienen registro si traen al menos una fila.
 */
export function seccionTieneRegistro(idSeccion, secciones) {
    const s = secciones || {};
    if (idSeccion === "motivo_consulta") return esTextoConContenido(s.motivo);
    return lista(s[idSeccion]).length > 0;
}

/** Nº de registros de la sección (1 para motivo_consulta cuando existe). */
export function conteoRegistros(idSeccion, secciones) {
    const s = secciones || {};
    if (idSeccion === "motivo_consulta") return esTextoConContenido(s.motivo) ? 1 : 0;
    return lista(s[idSeccion]).length;
}

/**
 * Divide el catálogo en las dos zonas del expediente: las secciones que el
 * médico sí registró (una tarjeta cada una) y las que no (una sola fila dentro
 * del bloque agrupado, nunca una tarjeta ni un control deshabilitado).
 */
export function particionarSecciones(secciones) {
    const conRegistro = [];
    const sinRegistro = [];
    for (const seccion of SECCIONES_AUDITORIA) {
        (seccionTieneRegistro(seccion.id, secciones) ? conRegistro : sinRegistro).push(seccion);
    }
    return { conRegistro, sinRegistro };
}

/**
 * Secciones tocadas por alguna corrección de auditoría, leídas del historial
 * real (`detalles[].section`) -- nunca inferidas del contenido actual.
 */
export function seccionesCorregidas(historial) {
    const ids = new Set();
    for (const correccion of lista(historial)) {
        for (const detalle of lista(correccion.detalles)) {
            if (detalle?.section) ids.add(detalle.section);
        }
    }
    return ids;
}

/**
 * Última corrección registrada sobre una sección concreta (para el badge
 * "Corregido por … · hora"). El historial llega ordenado created_at DESC, pero
 * se compara por fecha para no depender del orden del servidor.
 */
export function ultimaCorreccionDeSeccion(historial, idSeccion) {
    let mejor = null;
    for (const correccion of lista(historial)) {
        const tocaLaSeccion = lista(correccion.detalles).some((d) => d?.section === idSeccion);
        if (!tocaLaSeccion) continue;
        const cuando = correccion.created_at ? new Date(correccion.created_at).getTime() : 0;
        if (!mejor || cuando > mejor.cuando) {
            mejor = { cuando, created_at: correccion.created_at ?? null, actor: correccion.actor ?? null };
        }
    }
    return mejor ? { created_at: mejor.created_at, actor: mejor.actor } : null;
}

/**
 * Progreso REAL del expediente. Deliberadamente NO existe un contador de
 * "secciones revisadas": no hay dónde persistir un checklist de verificación
 * (ver FASE 0 §6), y un contador inventado sería una promesa falsa. Se informa
 * lo que sí es dato: cuántas secciones tienen registro clínico y cuántas de
 * ellas se corrigieron.
 */
export function resumenProgreso(secciones, historial) {
    const { conRegistro } = particionarSecciones(secciones);
    const corregidas = seccionesCorregidas(historial);
    return {
        total: TOTAL_SECCIONES,
        conRegistro: conRegistro.length,
        sinRegistro: TOTAL_SECCIONES - conRegistro.length,
        corregidas: conRegistro.filter((s) => corregidas.has(s.id)).length,
    };
}

/** Aplana el historial a una lista de cambios individuales, el más reciente primero. */
export function cambiosRecientes(historial, limite = 4) {
    const cambios = [];
    for (const correccion of lista(historial)) {
        for (const detalle of lista(correccion.detalles)) {
            cambios.push({
                id: `${correccion.id}-${detalle.id}`,
                section: detalle.section,
                operation: detalle.operation || "UPDATE",
                reason_code: detalle.reason_code || correccion.reason_code || null,
                before_json: detalle.before_json ?? null,
                after_json: detalle.after_json ?? null,
                actor: correccion.actor ?? null,
                created_at: correccion.created_at ?? null,
            });
        }
    }
    cambios.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    return typeof limite === "number" ? cambios.slice(0, limite) : cambios;
}
